import React, { useEffect, useRef } from 'react';
import './ConversationFilter.scss';
import { CategoryFilterItem, SentimentOption } from '../../shared/types';

interface ConversationFilterProps {
  categories: CategoryFilterItem[];
  categoryFilters: string[];
  sentimentFilter: SentimentOption;
  onCategoryFiltersChange: (filters: string[]) => void;
  onSentimentFilterChange: (filter: SentimentOption) => void;
  onClear: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

const SENTIMENT_OPTIONS: { value: SentimentOption; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
];

const ConversationFilter: React.FC<ConversationFilterProps> = ({
  categories,
  categoryFilters,
  sentimentFilter,
  onCategoryFiltersChange,
  onSentimentFilterChange,
  onClear,
  isOpen,
  onToggle,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onToggle();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onToggle]);

  const toggleCategory = (key: string) => {
    if (categoryFilters.includes(key)) {
      onCategoryFiltersChange(categoryFilters.filter((k) => k !== key));
    } else {
      onCategoryFiltersChange([...categoryFilters, key]);
    }
  };

  const filterLabel =
    categoryFilters.length > 0
      ? `${categoryFilters.length} selected`
      : sentimentFilter !== 'all'
      ? sentimentFilter.charAt(0).toUpperCase() + sentimentFilter.slice(1)
      : 'All';

  return (
    <div className="ca-conv-filter">
      <div className="ca-conv-filter__trigger-wrap">
        <button
          className={`ca-conv-filter__btn${isOpen ? ' ca-conv-filter__btn--open' : ''}`}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          type="button"
        >
          <span className="material-icons" aria-hidden="true">
            tune
          </span>
          <span className="ca-conv-filter__btn-label">Filter By:</span>
          <span className="ca-conv-filter__btn-value">{filterLabel}</span>
          <span className="material-icons" aria-hidden="true">
            expand_more
          </span>
        </button>

        {isOpen && (
          <>
            <div className="ca-conv-filter__overlay" onClick={onToggle} aria-hidden="true" />
            <div
              className="ca-conv-filter__dropdown"
              role="dialog"
              aria-label="Filter messages"
              ref={dropdownRef}
            >
              {categories.length > 0 && (
                <div className="ca-conv-filter__section">
                  <div className="ca-conv-filter__section-label">FILTER BY CATEGORY</div>
                  {categories.map((cat) => {
                    const checked = categoryFilters.includes(cat.key);
                    return (
                      <button
                        key={cat.key}
                        className="ca-conv-filter__row"
                        onClick={() => toggleCategory(cat.key)}
                        role="checkbox"
                        aria-checked={checked}
                        type="button"
                      >
                        <span
                          className={`ca-conv-filter__checkbox${
                            checked ? ' ca-conv-filter__checkbox--checked' : ''
                          }`}
                        >
                          {checked && (
                            <span className="material-icons" aria-hidden="true">
                              check
                            </span>
                          )}
                        </span>
                        <span className="ca-conv-filter__row-label">{cat.displayName}</span>
                        <span className="ca-conv-filter__row-count">{cat.count}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="ca-conv-filter__section ca-conv-filter__section--bordered">
                <div className="ca-conv-filter__section-label">FILTER BY SENTIMENT</div>
                <div
                  className="ca-conv-filter__sentiment-seg"
                  role="radiogroup"
                  aria-label="Sentiment filter"
                >
                  {SENTIMENT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={`ca-conv-filter__seg-btn${
                        sentimentFilter === opt.value ? ' ca-conv-filter__seg-btn--active' : ''
                      }`}
                      onClick={() => onSentimentFilterChange(opt.value)}
                      role="radio"
                      aria-checked={sentimentFilter === opt.value}
                      type="button"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                className="ca-conv-filter__clear"
                onClick={() => {
                  onClear();
                  onToggle();
                }}
                type="button"
              >
                Clear all filters
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationFilter;
